import { WarrantyDetails } from "@dune/types";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { formatCurrencyPrecise } from "@dune/utils";
import { ColumnDef } from "@tanstack/react-table";

export const binderWarrantiesColumns: ColumnDef<WarrantyDetails>[] = [
	{
		accessorKey: "name",
		header: "Garanties",
		cell: ({ row }) => {
			return (
				<div className="w-1/3">
					<p className="font-normal">{row.original.name}</p>
					<p className="italic font-thin">{row.original.article}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "limit",
		header: "Limites",
		cell: ({ row }) => {
			const { warrantyLimit, warrantyLimitOptions, limitIsUpdatable } =
				row.original;
			const { limitType, value, limitValue } = warrantyLimit || {};
			return (
				<div>
					{limitIsUpdatable && warrantyLimit?.id ? (
						<Select defaultValue={String(warrantyLimit.id)} disabled>
							<SelectTrigger size="none" className="disabled:opacity-100">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{warrantyLimitOptions?.map((option) => {
									const { id, limitType, value, limitValue } =
										option.limitOption;
									return (
										<SelectItem value={String(id)} key={id}>
											<WarrantyLimitText
												limitType={limitType}
												value={value}
												limitValue={limitValue}
											/>
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>
					) : (
						<WarrantyLimitText
							limitType={limitType}
							value={value}
							limitValue={limitValue}
						/>
					)}
				</div>
			);
		},
	},
	{
		accessorKey: "franchise",
		header: "Franchises",
		cell: ({ row }) => {
			const {
				warrantyFranchise,
				warrantyFranchiseOptions,
				franchiseIsUpdatable,
			} = row.original;
			const { value, comment } = warrantyFranchise || {};
			return (
				<div>
					{franchiseIsUpdatable && warrantyFranchise?.id ? (
						<Select defaultValue={String(warrantyFranchise.id)} disabled>
							<SelectTrigger size="none" className="disabled:opacity-100">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{warrantyFranchiseOptions.map((option) => {
									const { id, value, comment } = option.franchiseOption;
									return (
										<SelectItem value={String(id)} key={id}>
											<WarrantyFranchiseText value={value} comment={comment} />
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>
					) : (
						<WarrantyFranchiseText value={value} comment={comment} />
					)}
				</div>
			);
		},
	},
];

const WarrantyLimitText = ({
	limitType,
	value,
	limitValue,
}: {
	limitType?: string;
	value?: number | null;
	limitValue?: number | null;
}) => {
	if (!limitType) return null;
	return (
		<div className="text-wrap">
			{limitType === "NOT_PROPOSED" && "Non proposée"}
			{limitType === "NOT_APPLICABLE" && "Non applicable"}
			{limitType === "NOT_SUBSCRIBED" && "Non souscrite"}
			{limitType === "FIXED" &&
				value !== undefined &&
				value !== null &&
				formatCurrencyPrecise(value)}
			{limitType === "PERCENTAGE" &&
				`${value}% du montant des travaux ${limitValue && `dans la limite de ${formatCurrencyPrecise(limitValue)}`}`}
			{limitType === "CONCURRENCY" &&
				"A concurrence du coût total des travaux de remise en état de l'ouvrage"}
			{limitType === "OPTIONNAL_WARRANTY_LIMIT" &&
				"Dans les limites prévues au titres des garanties Facultatives de dommages à l'ouvrage"}
			{limitType === "CONSTRUCTION_COST_TAG" && "<<coût de constructon>>"}
			{limitType === "CONSTRUCTION_COST_PERCENTAGE" &&
				`${value}% du coût de construction`}
			{limitType === "CONSTRUCTION_COST_DECLARED" &&
				"Dans les limites prévues au titres des garanties Facultatives de dommages à l'ouvrage"}
			{limitType === "TOTAL_COST" &&
				"A hauteur du montant de l'ensemble des travaux afférents à la remise en état des ouvrages ou éléments d'équipement de l'opération de construction"}
			{limitType === "CONCURRENCY_PERCENTAGE" &&
				`A concurrence de ${value}% du coût total de l'opération de construction prévisionnel`}
			{limitType === "DATE" && `${value} mois après réception`}
			{limitType === "EXCLUDE" && "Exclus"}
		</div>
	);
};

const WarrantyFranchiseText = ({
	value,
	comment,
}: { value?: number | null; comment?: string | null }) => {
	return (
		<div className="text-wrap">
			{value !== null && value !== undefined
				? `${formatCurrencyPrecise(value)}`
				: ""}
			{comment && ` ${comment}`}
		</div>
	);
};
